import { Dialog, Transition } from '@headlessui/react';
import {
  CameraIcon,
  ClockIcon,
  CogIcon,
  CollectionIcon,
  CubeTransparentIcon,
  FilmIcon,
  HeartIcon,
  HomeIcon,
  LogoutIcon,
  MenuAlt2Icon,
  MicrophoneIcon,
  PencilIcon,
  QuestionMarkCircleIcon,
  SpeakerphoneIcon,
  UserCircleIcon,
  VideoCameraIcon,
  XIcon,
  MusicNoteIcon,
  ViewGridAddIcon,
} from '@heroicons/react/outline';
import {
  ChatAlt2Icon,
  DocumentTextIcon,
  VolumeUpIcon,
} from '@heroicons/react/solid';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import * as React from 'react';
import { Fragment, useState } from 'react';
import { FaImages, FaRobot } from 'react-icons/fa6';
import { GiSofa, GiSoundWaves } from 'react-icons/gi';

import clsxm from '@/lib/clsxm';
import { useDashboardChromePresence } from '@/lib/siteChatVisibility';

import { logout } from '@/utils/auth';
import { useUser } from '@/utils/useUser';

import ParityBanner from '../atoms/ParityBanner';
// import Banner from '../atoms/Banner';
import ButtonLink from '../links/ButtonLink';
import { FaPhotoVideo } from 'react-icons/fa';
import { RiRecordCircleFill } from 'react-icons/ri';
import { LanguageSelector } from './LanguageSelector';
import { ThemeSelector } from './ThemeSelector';
import WhatsNewButton from '../atoms/WhatsNewButton';
import UserDropdown from '../atoms/UserDropdown';
import ToolsFlyout from './ToolsFlyout';
import NavPinButton from './NavPinButton';
import { useFavoriteTools } from '@/hooks/useFavoriteTools';
import { getPresetToolHref, presets } from '@/constants/presets';
import { ImHistory } from 'react-icons/im';
// import Banner from '../atoms/Banner'; // Black Friday is over

export const navigation = [
  {
    name: 'Home',
    href: '/dashboard',
    icon: HomeIcon,
    slug: 'home',
    cannotUnpin: true,
  },
  {
    name: 'Tools',
    href: '/presets',
    icon: CollectionIcon,
    slug: 'tools',
    cannotUnpin: true,
  },
  // { name: 'Favourites', href: '/history/favorites', icon: HeartIcon, slug: 'favourites' },
  {
    name: 'Marky Agent',
    href: '/chat',
    icon: ChatAlt2Icon,
    slug: 'chat',
    badge: (
      <div className='ml-2 hidden rounded-full bg-primary-500 px-1.5 py-0.5 text-xs text-white sm:block'>
        Updated
      </div>
    ),
  },
  // AI Employees: hidden during closed testing — the feature is reachable only
  // by direct link (/employees). Uncomment at launch.
  // {
  //   name: 'AI Employees',
  //   href: '/employees',
  //   icon: FaUserTie,
  //   slug: 'employees',
  //   badge: (
  //     <div className='ml-2 hidden rounded-full bg-primary-500 px-1.5 py-0.5 text-xs text-white sm:block'>
  //       New
  //     </div>
  //   ),
  // },
  {
    name: 'Bots',
    href: '/agents',
    icon: FaRobot,
    slug: 'bots',
    // Transitional hint after the Bots -> Agents rename; remove once users are used to the new name
    badge: (
      <div className='ml-2 hidden rounded-full bg-gray-200 px-1.5 py-0.5 text-xs text-gray-600 dark:bg-slate-700 dark:text-slate-300 sm:block'>
        ex. Bots
      </div>
    ),
  },
  {
    name: 'Workflows',
    href: '/workflows',
    icon: ViewGridAddIcon,
    slug: 'workflows',
    badge: (
      <div className='ml-2 hidden rounded-full bg-primary-500 px-1.5 py-0.5 text-xs text-white sm:block'>
        Beta
      </div>
    ),
  },
  {
    name: 'AI Images',
    href: '/ai-images',
    icon: FaImages,
    slug: 'ai-images',
    // badge: (
    //   <div className='ml-2 hidden rounded-full bg-primary-500 px-1.5 py-0.5 text-xs text-white sm:block'>
    //     Upgraded
    //   </div>
    // ),
  },
  {
    name: 'Sprite Generator',
    href: '/sprite-generator',
    icon: CubeTransparentIcon,
    slug: 'sprite-generator',
    badge: (
      <div className='ml-2 hidden rounded-full bg-primary-500 px-1.5 py-0.5 text-xs text-white sm:block'>
        New
      </div>
    ),
  },
  {
    name: 'AI Image Editor',
    href: '/ai-image-editor',
    icon: PencilIcon,
    slug: 'ai-image-editor',
  },
  {
    name: 'AI Video Editor',
    href: '/ai-video-editor',
    icon: FilmIcon,
    slug: 'ai-video-editor',
    badge: (
      <div className='ml-2 hidden rounded-full bg-primary-500 px-1.5 py-0.5 text-xs text-white sm:block'>
        New
      </div>
    ),
  },
  {
    name: 'AI Videos',
    href: '/ai-videos',
    icon: VideoCameraIcon,
    slug: 'ai-videos',
    badge: (
      <div className='ml-2 hidden rounded-full bg-primary-500 px-1.5 py-0.5 text-xs text-white sm:block'>
        New
      </div>
    ),
  },
  {
    name: 'AI Talking Videos',
    href: '/ai-talking-videos',
    icon: UserCircleIcon,
    slug: 'ai-talking-videos',
    badge: (
      <div className='ml-2 hidden rounded-full bg-primary-500 px-1.5 py-0.5 text-xs text-white sm:block'>
        2.0
      </div>
    ),
  },
  {
    name: 'AI Text to Speech',
    href: '/tts',
    icon: MicrophoneIcon,
    slug: 'tts',
  },
  {
    name: 'AI Speech to Text',
    href: '/audios',
    icon: GiSoundWaves,
    slug: 'audios',
    className: '',
  },
  // record audio
  {
    name: 'Record & Transcribe',
    href: '/note-taker',
    icon: RiRecordCircleFill,
    slug: 'note-taker',
  },
  // {
  //   name: 'AI Sound Effects',
  //   href: '/sound-effects',
  //   icon: VolumeUpIcon,
  //   badge: (
  //     <div className='ml-2 hidden rounded-full bg-primary-500 px-1.5 py-0.5 text-xs text-white sm:block'>
  //       New
  //     </div>
  //   ),
  // },
  {
    name: 'AI Music Generator',
    href: '/music-generator',
    icon: MusicNoteIcon,
    slug: 'music-generator',
    badge: (
      <div className='ml-2 hidden rounded-full bg-primary-500 px-1.5 py-0.5 text-xs text-white sm:block'>
        2.0
      </div>
    ),
  },

  // {
  //   name: 'Workflows',
  //   href: '/workflows',
  //   icon: FaListOl,
  //   // badge: (
  //   //   <div className='ml-2 hidden rounded-full bg-primary-500 px-1.5 py-0.5 text-xs text-white sm:block'>
  //   //     New
  //   //   </div>
  //   // ),
  // },
  // { name: 'Long-form editor', href: '/documents', icon: DocumentTextIcon },
  {
    name: 'AI Interior Designer',
    href: '/ai-interior-designer',
    icon: GiSofa,
    slug: 'ai-interior-designer',
  },
  {
    name: 'AI Photo Studio',
    href: '/ai-images/models',
    icon: FaPhotoVideo,
    slug: 'ai-images-models',
    badge: (
      <div className='ml-2 hidden rounded-full bg-primary-500 px-1.5 py-0.5 text-xs text-white sm:block'>
        New
      </div>
    ),
  },
  {
    name: 'Headshots',
    href: '/headshots',
    icon: CameraIcon,
    slug: 'headshots',
    badge: (
      <div className='ml-2 hidden rounded-full bg-primary-500 px-1.5 py-0.5 text-xs text-white sm:block'>
        New
      </div>
    ),
  },

  // {
  //   name: 'Avatar AI',
  //   href: '/avatars',
  //   icon: PhotographIcon,
  // },

  // {
  //   name: 'Favorites',
  //   href: '/favorites',
  //   icon: HeartIcon,
  //   className: 'mt-10',
  // },

  {
    name: 'Brand Voice',
    href: '/voices',
    icon: SpeakerphoneIcon,
    slug: 'voices',
  },

  // Additional tools from flyout menu
  {
    name: 'Magic Upscaler',
    href: '/magic-upscaler',
    icon: FaImages,
    slug: 'magic-upscaler',
  },
  {
    name: 'AI Audio Generator',
    href: '/audio-generator',
    icon: VolumeUpIcon,
    slug: 'audio-generator',
  },
  {
    name: 'Sound Effect Generator',
    href: '/sound-effects',
    icon: VolumeUpIcon,
    slug: 'sound-effects',
  },
  {
    name: 'Remove Background',
    href: '/ai-images?action=remove-background',
    icon: FaImages,
    slug: 'remove-background',
  },
  {
    name: 'Replace Background',
    href: '/ai-images?action=replace-background',
    icon: FaImages,
    slug: 'replace-background',
  },

  // {
  //   name: 'Team',
  //   href: '/settings/team',
  //   icon: UsersIcon,
  // },
  // { name: 'Trash', href: '/trash', icon: TrashIcon },

  // { name: 'Settings', href: '/settings', icon: CogIcon },
];

export default function DashboardLayout({
  className,
  children,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const { t } = useLocale('dashboard');
  const router = useRouter();
  // Tells the site-wide support bubble it is inside the product (hidden there
  // except on Support/Settings — see SiteChatWidget).
  useDashboardChromePresence();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isFreePlan, user } = useUser();
  const { favoriteTools, toggleFavorite } = useFavoriteTools();
  const [toolsFlyoutOpen, setToolsFlyoutOpen] = useState(false);
  const [hoveredToolsItem, setHoveredToolsItem] = useState(false);
  const [toolsItemTop, setToolsItemTop] = useState(0);
  const toolsItemRef = React.useRef<HTMLDivElement>(null);
  const flyoutCloseTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Create navigation items from favorite presets (top 10)
  const favoriteNavigationItems = React.useMemo(() => {
    // Get all existing navigation slugs to avoid duplicates
    const navigationSlugs = navigation.map((item) => item.slug);

    const items: Array<{
      name: string;
      href: string;
      icon: any;
      slug: string;
      badge?: React.ReactNode;
      className?: string;
    }> = [];

    favoriteTools
      .filter((slug) => !navigationSlugs.includes(slug)) // Exclude items already in navigation
      .slice(0, 10) // Top 10
      .forEach((slug) => {
        const preset = presets.find((p) => p.slug === slug);
        if (preset) {
          items.push({
            name: preset.title,
            href: getPresetToolHref(preset),
            icon: preset.icon,
            slug: preset.slug,
          });
        }
      });

    return items;
  }, [favoriteTools]);

  // Merge navigation with favorite items
  const allNavigation = React.useMemo(() => {
    // Find the index of "Favourites" item
    const favouritesIndex = navigation.findIndex(
      (item) => item.slug === 'favourites',
    );

    if (favouritesIndex === -1) {
      // If no Favourites item, just append to the end before additional tools
      const toolsStartIndex =
        navigation.findIndex((item) => item.slug === 'voices') + 1;
      return [
        ...navigation.slice(0, toolsStartIndex),
        ...favoriteNavigationItems,
        ...navigation.slice(toolsStartIndex),
      ];
    }

    // Insert favorite items after the "Favourites" nav item
    return [
      ...navigation.slice(0, favouritesIndex + 1),
      ...favoriteNavigationItems,
      ...navigation.slice(favouritesIndex + 1),
    ];
  }, [favoriteNavigationItems]);

  // Filter navigation items based on pinned status
  const visibleNavigation = React.useMemo(() => {
    // Get slugs of favorite navigation items to always show them
    const favoriteItemSlugs = favoriteNavigationItems.map((item) => item.slug);

    return allNavigation.filter((item) => {
      // @ts-ignore - cannotUnpin exists on items now
      if (item.cannotUnpin) {
        // Always show items that cannot be unpinned (Home, Tools)
        return true;
      }

      if (!user) {
        // For non-logged-in users, show all items except Home
        return item.name !== 'Home';
      }

      // Always show favorite items (check favoriteTools directly)
      // @ts-ignore - slug exists on items now
      return favoriteTools.includes(item.slug);
    });
  }, [allNavigation, user, favoriteNavigationItems, favoriteTools]);

  React.useEffect(() => {
    const dialoqBtn = document.getElementById('dialoq-btn');
    if (dialoqBtn) {
      dialoqBtn.style.display = 'none';
    }
  }, []);

  return (
    // translate='no': Chrome auto-translate wraps text nodes in <font> tags
    // and breaks React reconciliation (insertBefore/removeChild
    // NotFoundError — ~120/day in PostHog across tts/music/sound-effects).
    // The app ships 13 locales natively; public SEO pages stay translatable.
    <div
      translate='no'
      className={clsxm(
        'notranslate relative flex h-screen sm:overflow-hidden',
        className,
      )}
    >
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as='div'
          className='fixed inset-0 z-40 flex md:hidden'
          onClose={setSidebarOpen}
        >
          <Transition.Child
            as={Fragment}
            enter='transition-opacity ease-linear duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='transition-opacity ease-linear duration-300'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <Dialog.Overlay className='fixed inset-0 bg-gray-600 bg-opacity-75' />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter='transition ease-in-out duration-300 transform'
            enterFrom='-translate-x-full'
            enterTo='translate-x-0'
            leave='transition ease-in-out duration-300 transform'
            leaveFrom='translate-x-0'
            leaveTo='-translate-x-full'
          >
            <div
              className='relative flex w-full max-w-xs flex-1 flex-col pb-4 pt-5 backdrop-blur-sm transition-all duration-1000 [background-color:rgba(255,255,255,var(--sidebar-bg-opacity,1))] dark:[background-color:rgba(30,41,59,var(--sidebar-bg-opacity,1))]'
              style={{ opacity: 'var(--sidebar-opacity, 1)' }}
            >
              <Transition.Child
                as={Fragment}
                enter='ease-in-out duration-300'
                enterFrom='opacity-0'
                enterTo='opacity-100'
                leave='ease-in-out duration-300'
                leaveFrom='opacity-100'
                leaveTo='opacity-0'
              >
                <div className='absolute right-0 top-0 -mr-14 p-1'>
                  <button
                    type='button'
                    className='flex h-12 w-12 items-center justify-center rounded-full focus:bg-gray-600 focus:outline-none'
                    onClick={() => setSidebarOpen(false)}
                  >
                    <XIcon className='h-6 w-6 text-white' aria-hidden='true' />
                    <span className='sr-only'>Close sidebar</span>
                  </button>
                </div>
              </Transition.Child>
              <div className='flex flex-shrink-0 items-center px-4 text-2xl font-extrabold text-primary-700'>
                <Link href={user ? '/dashboard' : '/'} passHref>
                  <Image
                    src='/images/marky.png'
                    width='250'
                    height='36'
                    alt='Easy-Peasy.ai logo'
                  />
                </Link>
              </div>

              <div className='flex items-center gap-3 px-4 mt-4 mb-2'>
                <LanguageSelector className='relative' align='left' />
                <ThemeSelector className='relative' align='left' />
                <WhatsNewButton className='relative' />
                <UserDropdown className='relative' align='left' />
              </div>

              <div className='mt-2 h-0 flex-1 overflow-y-auto'>
                <nav className='flex h-full flex-col'>
                  <div className='space-y-1'>
                    {visibleNavigation.map((item) => {
                      // @ts-ignore - slug exists on items now
                      const isToolsItem = item.slug === 'tools';
                      const ItemContent = (
                        <>
                          <item.icon
                            className={clsxm(
                              router.pathname.includes(item.href)
                                ? 'text-primary-500'
                                : 'text-gray-400 group-hover:text-gray-500',
                              'mr-4 h-6 w-6 flex-shrink-0',
                            )}
                            aria-hidden='true'
                          />
                          {t(item.name) || item.name}
                          {/* @ts-ignore - cannotUnpin exists on items now */}
                          {user && !item.cannotUnpin && (
                            <NavPinButton
                              // @ts-ignore - slug exists
                              slug={item.slug}
                              // @ts-ignore - slug exists
                              isFavorite={favoriteTools.includes(item.slug)}
                              onToggleFavorite={toggleFavorite}
                              showOnHover={true}
                            />
                          )}
                        </>
                      );

                      return (
                        <Link
                          passHref
                          key={item.name}
                          href={item.href}
                          className={clsxm(
                            router.pathname.includes(item.href)
                              ? 'border-primary-600 bg-primary-50 text-primary-600'
                              : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-300',
                            'group flex items-center border-l-4 px-3 py-2 text-base font-medium',
                            item.className,
                          )}
                          aria-current={
                            router.pathname.includes(item.href)
                              ? 'page'
                              : undefined
                          }
                        >
                          {ItemContent}
                        </Link>
                      );
                    })}
                  </div>
                  {user ? (
                    <div className='mt-auto space-y-1 pt-10'>
                      {isFreePlan && (
                        <ButtonLink
                          href='/settings/billing'
                          variant='dark'
                          className='mb-10 ml-5'
                        >
                          {t('Upgrade plan')}
                        </ButtonLink>
                      )}
                      <Link
                        href='/history'
                        className='group flex cursor-pointer items-center border-l-4 border-transparent px-3 py-2 text-sm font-medium text-gray-600 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-300'
                      >
                        <ImHistory
                          className='mr-3 h-6 w-6 text-gray-400 group-hover:text-gray-500 dark:text-slate-400'
                          aria-hidden='true'
                        />
                        {t('History')}
                      </Link>
                      <Link
                        href='/settings'
                        className='group flex cursor-pointer items-center border-l-4 border-transparent px-3 py-2 text-sm font-medium text-gray-600 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-300'
                      >
                        <CogIcon
                          className='mr-3 h-6 w-6 text-gray-400 group-hover:text-gray-500 dark:text-slate-400'
                          aria-hidden='true'
                        />
                        {t('Settings')}
                      </Link>
                      <Link
                        href='/support'
                        className='group flex cursor-pointer items-center border-l-4 border-transparent px-3 py-2 text-sm font-medium text-gray-600 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-300'
                      >
                        <QuestionMarkCircleIcon
                          className='mr-3 h-6 w-6 text-gray-400 group-hover:text-gray-500 dark:text-slate-400'
                          aria-hidden='true'
                        />
                        {t('Help & Support')}
                      </Link>

                      <a
                        onClick={logout}
                        className='group flex items-center border-l-4 border-transparent px-3 py-2 text-base font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-300'
                      >
                        <LogoutIcon
                          className='mr-3 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                          aria-hidden='true'
                        />
                        {t('Logout')}
                      </a>
                    </div>
                  ) : (
                    <div className='mt-auto space-y-1 pt-10'>
                      <Link
                        href='/login'
                        className='group flex items-center border-l-4 border-transparent px-3 py-2 text-base font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-300'
                      >
                        <UserCircleIcon
                          className='mr-3 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                          aria-hidden='true'
                        />
                        {t('Login')}
                      </Link>
                    </div>
                  )}
                </nav>
              </div>
            </div>
          </Transition.Child>
          <div className='w-14 flex-shrink-0' aria-hidden='true'>
            {/* Dummy element to force sidebar to shrink to fit close icon */}
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div className='hidden md:flex-shrink-0 md:[display:var(--sidebar-display,flex)] transition-all duration-1000'>
        <div className='relative flex w-64 flex-col border-r border-gray-200 bg-gray-50 pt-5 dark:border-slate-200/5 dark:bg-slate-800'>
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className='flex flex-shrink-0 items-center px-4 text-2xl font-extrabold text-primary-700'>
            <Link href={user ? '/dashboard' : '/'} passHref>
              <Image
                src='/images/marky.png'
                width='250'
                height='36'
                alt='Easy-Peasy.ai logo'
              />
            </Link>
          </div>

          <div className='flex items-center gap-3 px-4 mt-4 mb-4'>
            <LanguageSelector className='relative' align='left' />
            <ThemeSelector className='relative' align='left' />
            <WhatsNewButton className='relative' />
            <UserDropdown className='relative' align='left' />
          </div>

          <nav className='flex flex-grow flex-col overflow-y-auto pb-4'>
            <div className='flex flex-grow flex-col'>
              <div className='flex-1 space-y-1'>
                {visibleNavigation.map((item) => {
                  // @ts-ignore - slug exists on items now
                  const isToolsItem = item.slug === 'tools';

                  return (
                    <div
                      key={item.name}
                      ref={isToolsItem ? toolsItemRef : null}
                      className='relative'
                      onMouseEnter={() => {
                        if (isToolsItem) {
                          // Clear any pending close timeout
                          if (flyoutCloseTimeoutRef.current) {
                            clearTimeout(flyoutCloseTimeoutRef.current);
                            flyoutCloseTimeoutRef.current = null;
                          }

                          // Calculate position relative to sidebar
                          if (toolsItemRef.current) {
                            const rect =
                              toolsItemRef.current.getBoundingClientRect();
                            const sidebarTop =
                              toolsItemRef.current
                                .closest('.flex.w-64')
                                ?.getBoundingClientRect().top || 0;
                            setToolsItemTop(rect.top - sidebarTop);
                          }
                          setToolsFlyoutOpen(true);
                          setHoveredToolsItem(true);
                        }
                      }}
                      onMouseLeave={() => {
                        if (isToolsItem) {
                          setHoveredToolsItem(false);
                          // Delay closing to allow moving to flyout
                          flyoutCloseTimeoutRef.current = setTimeout(() => {
                            setToolsFlyoutOpen(false);
                          }, 300);
                        }
                      }}
                    >
                      <Link
                        href={item.href}
                        className={clsxm(
                          router.pathname.includes(item.href)
                            ? 'border-primary-600 bg-primary-50 text-primary-600 dark:bg-slate-900/50'
                            : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-300',
                          'group flex items-center border-l-4 px-3 py-2 text-sm font-medium',
                          item.className,
                        )}
                        passHref
                      >
                        <item.icon
                          className={clsxm(
                            router.pathname.includes(item.href)
                              ? 'text-primary-500'
                              : 'text-gray-400 group-hover:text-gray-500',
                            'mr-3 h-6 w-6 flex-shrink-0',
                          )}
                          aria-hidden='true'
                        />
                        {t(item.name)} {item.badge && item.badge}
                        {/* @ts-ignore - cannotUnpin exists on items now */}
                        {user && !item.cannotUnpin && (
                          <NavPinButton
                            // @ts-ignore - slug exists
                            slug={item.slug}
                            // @ts-ignore - slug exists
                            isFavorite={favoriteTools.includes(item.slug)}
                            onToggleFavorite={toggleFavorite}
                            showOnHover={true}
                          />
                        )}
                      </Link>
                    </div>
                  );
                })}
              </div>
            </div>
            {user ? (
              <div className='block w-full flex-shrink-0'>
                {/* <div className='relative m-3 hidden space-y-4 rounded-lg bg-white px-2 py-3 text-neutral-900 dark:bg-slate-700 md:block'>
                  <div className='flex flex-col space-y-2'>
                    <div>
                      <span className='mr-1'>🎉</span>
                      <span className='text-sm font-medium dark:text-slate-300'>
                        {t('Refer a friend')}
                      </span>
                    </div>
                    <p className='text-xs text-gray-600 dark:text-slate-400'>
                      {t(
                        'Refer your friends to Easy-Peasy.AI and you both will receive 20,000 bonus words.'
                      )}
                    </p>
                    <UnstyledLink
                      href='/settings/referrals'
                      className='text-md text-primary-500'
                    >
                      {t('Click here to explore')}
                    </UnstyledLink>
                  </div>
                </div> */}
                {isFreePlan && (
                  <ButtonLink
                    href='/settings/billing'
                    variant='dark'
                    className='mb-10 ml-5'
                  >
                    {t('Upgrade plan')}
                  </ButtonLink>
                )}
                <Link
                  href='/history'
                  className='group flex cursor-pointer items-center border-l-4 border-transparent px-3 py-2 text-sm font-medium text-gray-600 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-300'
                >
                  <ImHistory
                    className='mr-3 h-6 w-6 text-gray-400 group-hover:text-gray-500 dark:text-slate-400'
                    aria-hidden='true'
                  />
                  {t('History')}
                </Link>
                <Link
                  href='/settings'
                  className='group flex cursor-pointer items-center border-l-4 border-transparent px-3 py-2 text-sm font-medium text-gray-600 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-300'
                >
                  <CogIcon
                    className='mr-3 h-6 w-6 text-gray-400 group-hover:text-gray-500 dark:text-slate-400'
                    aria-hidden='true'
                  />
                  {t('Settings')}
                </Link>
                <Link
                  href='/support'
                  className='group flex cursor-pointer items-center border-l-4 border-transparent px-3 py-2 text-sm font-medium text-gray-600 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-300'
                >
                  <QuestionMarkCircleIcon
                    className='mr-3 h-6 w-6 text-gray-400 group-hover:text-gray-500 dark:text-slate-400'
                    aria-hidden='true'
                  />
                  {t('Help & Support')}
                </Link>

                <a
                  onClick={logout}
                  className='group flex cursor-pointer items-center border-l-4 border-transparent px-3 py-2 text-sm font-medium text-gray-600 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-300'
                >
                  <LogoutIcon
                    className='mr-3 h-6 w-6 text-gray-400 group-hover:text-gray-500 dark:text-slate-400'
                    aria-hidden='true'
                  />
                  {t('Logout')}
                </a>
              </div>
            ) : (
              <div className='block w-full flex-shrink-0'>
                <Link
                  href='/login'
                  className='group flex cursor-pointer items-center border-l-4 border-transparent px-3 py-2 text-sm font-medium text-gray-600 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-300'
                >
                  <UserCircleIcon
                    className='mr-3 h-6 w-6 text-gray-400 group-hover:text-gray-500 dark:text-slate-400'
                    aria-hidden='true'
                  />
                  {t('Login')}
                </Link>
              </div>
            )}
          </nav>

          {/* Tools Flyout Menu - Rendered outside nav to avoid overflow clipping */}
          {toolsFlyoutOpen && (
            <>
              {/* Invisible bridge to prevent flyout from closing when moving mouse */}
              <div
                className='absolute left-64 z-40 h-12 w-4'
                style={{ top: `${toolsItemTop}px` }}
                onMouseEnter={() => {
                  if (flyoutCloseTimeoutRef.current) {
                    clearTimeout(flyoutCloseTimeoutRef.current);
                    flyoutCloseTimeoutRef.current = null;
                  }
                }}
              />

              <div
                className='absolute z-50'
                style={{ left: '256px', top: `${toolsItemTop}px` }}
                onMouseEnter={() => {
                  // Clear any pending close timeout when entering flyout
                  if (flyoutCloseTimeoutRef.current) {
                    clearTimeout(flyoutCloseTimeoutRef.current);
                    flyoutCloseTimeoutRef.current = null;
                  }
                  setToolsFlyoutOpen(true);
                }}
                onMouseLeave={() => {
                  // Close immediately when leaving flyout
                  setToolsFlyoutOpen(false);
                }}
              >
                <ToolsFlyout
                  isOpen={toolsFlyoutOpen}
                  onMouseEnter={() => {
                    // Clear timeout when entering flyout content
                    if (flyoutCloseTimeoutRef.current) {
                      clearTimeout(flyoutCloseTimeoutRef.current);
                      flyoutCloseTimeoutRef.current = null;
                    }
                  }}
                  onMouseLeave={() => {
                    // This is handled by the parent div
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
      <div className='flex flex-1 flex-col bg-gray-100 dark:bg-slate-900 min-w-0'>
        {/* {isFreePlan && !router.pathname.includes('dashboard') && <Banner />} Black Friday is over */}
        <div className='mx-auto w-full max-w-4xl md:px-8 xl:px-0'>
          <button
            type='button'
            className='mt-1 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 md:hidden'
            onClick={() => setSidebarOpen(true)}
          >
            <span className='sr-only'>Open sidebar</span>
            <MenuAlt2Icon className='h-6 w-6' aria-hidden='true' />
          </button>
        </div>

        {children}
      </div>
    </div>
  );
}
